import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import * as React from "react";

/**
 * Carries the controlled `value` from `NavigationTabs` (root) down to
 * list components so they can detect genuine tab switches vs. layout-only
 * updates. Defined here (not in NavigationTabs.tsx) to avoid circular
 * imports — both NavigationTabs and ScrollableTabList consume this.
 */
export const TabValueContext = createContext<string | undefined>(undefined);

/**
 * Returns a ref that is `false` until the controlled tab value changes
 * (a genuine user-initiated tab switch), then `true`.
 *
 * Resets to `false` when the children count changes (tab added/removed)
 * so the indicator snaps to the new position without animating.
 *
 * When the parent `NavigationTabs` is uncontrolled (`value` prop omitted),
 * the ref is set to `true` after the first render so the indicator always
 * animates — we can't distinguish layout shifts from tab switches without
 * a controlled value.
 */
export function useIndicatorTransition(children: ReactNode): React.RefObject<boolean> {
    const tabValue = useContext(TabValueContext);
    const initialValueRef = useRef(tabValue);
    const enabledRef = useRef(false);

    // Flip the ref after React commits a render where the value differs
    // from the initial value. Using useEffect (not render-phase mutation)
    // ensures this is safe under React concurrent mode and fires after
    // the DOM commit — so MutationObservers that run on the same commit
    // will see the updated ref.
    useEffect(() => {
        if (tabValue === undefined) {
            // Uncontrolled mode — enable after first render since we can't
            // distinguish layout shifts from tab switches.
            enabledRef.current = true;
        } else if (tabValue !== initialValueRef.current) {
            enabledRef.current = true;
        }
    }, [tabValue]);

    // When children change (tab added/removed), reset so the indicator
    // snaps to the new position without animating.
    const prevChildCount = useRef<number | null>(null);
    const childCount = React.Children.count(children);
    useEffect(() => {
        if (prevChildCount.current !== null && prevChildCount.current !== childCount) {
            enabledRef.current = false;
            initialValueRef.current = tabValue;
        }
        prevChildCount.current = childCount;
    }, [childCount, tabValue]);

    return enabledRef;
}
